// client/src/components/LoadingSpinner.tsx
"use client";

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '@/config/theme';

interface WealthCoinSpinnerProps {
  size: number;
  showPound: boolean;
  isReducedMotion: boolean;
}

const WealthCoinSpinner: React.FC<WealthCoinSpinnerProps> = ({
  size,
  showPound,
  isReducedMotion
}) => {
  if (isReducedMotion) {
    return (
      <div 
        className="rounded-full flex items-center justify-center"
        style={{ 
          width: size, 
          height: size,
          background: `linear-gradient(135deg, ${colors.brand.positive} 0%, ${colors.brand.authority} 100%)`,
          border: `3px solid ${colors.brand.authority}`,
          boxShadow: `0 4px 8px ${colors.shadow.md}`,
        }}
      >
        {showPound && (
          <div
            className="font-bold"
            style={{ 
              fontSize: size * 0.55,
              color: '#ffffff',
              lineHeight: 1,
            }}
          >
            £
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div
      className="rounded-full flex items-center justify-center"
      style={{ 
        width: size, 
        height: size,
        background: `linear-gradient(135deg, ${colors.brand.positive} 0%, ${colors.brand.authority} 100%)`,
        border: `3px solid ${colors.brand.authority}`,
        boxShadow: `0 6px 12px ${colors.shadow.md}`,
      }}
      animate={{
        rotate: 360,
        scale: [1, 1.1, 1],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {showPound && (
        <motion.div
          className="font-bold"
          style={{ 
            fontSize: size * 0.55,
            color: '#ffffff',
            textShadow: '1px 1px 0px rgba(0,0,0,0.2)',
            lineHeight: 1,
          }}
          animate={{
            scale: [1, 0.9, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          £
        </motion.div>
      )}
    </motion.div>
  );
};

interface LoadingSpinnerProps {
  className?: string;
  size?: number;
  message?: string;
  showPound?: boolean;
}

const LoadingSpinner = ({ 
  className = '', 
  size = 60, 
  message,
  showPound = true 
}: LoadingSpinnerProps) => {
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsReducedMotion(true);
    }
    
    const handleChange = () =>  
      setIsReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
       
      setShowSpinner(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`fixed inset-0 flex flex-col items-center justify-center bg-white bg-opacity-75 z-50 ${className}`}
      style={{ pointerEvents: 'none' }}
      role="status"
      aria-live="polite"
      aria-label="Loading..."
      data-testid="loading-spinner"
    >
      <AnimatePresence mode="wait">
        {showSpinner && (
          <motion.div
            key="spinner"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className="relative"
          >
            <WealthCoinSpinner 
              size={size} 
              showPound={showPound} 
              isReducedMotion={isReducedMotion} 
            />
          </motion.div>
        )}
      </AnimatePresence>
      {message && (
        <motion.p
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: showSpinner ? 1 : 0, y: showSpinner ? 0 : 5 }}
          transition={{ duration: 0.2, delay: 0.05 }}
          className="mt-4 text-gray-600 font-medium text-sm"
        >
          {message}
        </motion.p>
      )}
    </div>
  );
};

export default LoadingSpinner;
